import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { useHistory } from 'react-router-dom';
import {
  PiPlusLight,
  PiTrashLight,
  PiTableLight,
  PiArrowLeftLight,
} from 'react-icons/pi';
import {
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTableTemplate,
} from '../../store/actions/BatchTables/BatchTablesActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig, OrgWideToggle } from '../shared/FormComponents';
import FormSkeleton from '../shared/FormComponents/FormSkeleton';
import { FormSchema } from '../../utils/validation/form-schema';
import { Collaborator } from '../shared/CollaboratorSelector';
import { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import { ColumnDataType } from '../../types/batchTables';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, tableColumnsFormTourSteps, tableColumnItemTourSteps } from '../shared/FeatureTour';

// ─── Column Editor Custom Component ────────────────────────────────────

interface ColumnItem {
  name: string;
  query: string;
  data_type: ColumnDataType;
}

const dataTypeOptions: DropdownOption[] = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'currency', label: 'Currency' },
];

interface ColumnsEditorProps {
  id?: string;
  name?: string;
  label?: string;
  value: ColumnItem[];
  onChange: (value: ColumnItem[]) => void;
  disabled?: boolean;
  required?: boolean;
  isGlobal?: boolean;
}

const ColumnsEditor: React.FC<ColumnsEditorProps> = ({
  value = [],
  onChange,
  disabled,
  isGlobal,
}) => {
  const { t } = useTranslation();
  const columns = value || [];
  const isReadOnly = disabled || isGlobal;

  const handleColumnChange = (index: number, field: keyof ColumnItem, val: string) => {
    const updated = [...columns];
    updated[index] = { ...updated[index], [field]: val };
    onChange(updated);
  };

  const addColumn = () => {
    onChange([...columns, { name: '', query: '', data_type: 'text' as ColumnDataType }]);
  };

  const removeColumn = (index: number) => {
    const updated = [...columns];
    updated.splice(index, 1);
    onChange(updated);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base text-gray-900 dark:text-white">
            {t('Columns')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {t('Define the columns (queries) that will be applied to each document.')}
          </p>
        </div>
      </div>

      {columns.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <PiTableLight className="w-10 h-10 mx-auto text-gray-400 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('No columns defined. Add your first column to get started.')}
          </p>
          {!isReadOnly && (
            <Button
              type="button"
              variant="neutral"
              size="small"
              onClick={addColumn}
              className="mt-3"
            >
              <PiPlusLight className="w-4 h-4 mr-1" />
              {t('Add Column')}
            </Button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {columns.map((col, index) => (
          <div
            key={index}
            className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 relative group"
            {...(index === 0 ? { 'data-tour-id': 'table-col-item' } : {})}
          >
            {/* Remove button - top right, hover reveal */}
            {!isReadOnly && (
              <button
                type="button"
                onClick={() => removeColumn(index)}
                className="absolute top-3 right-3 p-1.5 rounded-md text-gray-300 dark:text-gray-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition opacity-0 group-hover:opacity-100"
                title={t('Remove column')}
              >
                <PiTrashLight className="w-4 h-4" />
              </button>
            )}

            {/* Row 1: Column number + Name + Type */}
            <div className="flex items-start gap-3">
              <div className="flex items-center pt-2">
                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 bg-gray-200/60 dark:bg-gray-700/50 w-6 h-6 rounded-full flex items-center justify-center">
                  {index + 1}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <Input
                  id={`col-name-${index}`}
                  type="text"
                  label={t('Column Name')}
                  placeholder={t('e.g., Effective Date')}
                  value={col.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleColumnChange(index, 'name', e.target.value)
                  }
                  disabled={isReadOnly}
                />
              </div>
              <div className="flex-shrink-0">
                <Dropdown
                  id={`col-type-${index}`}
                  label={t('Type')}
                  options={dataTypeOptions}
                  value={col.data_type}
                  onChange={(val) =>
                    handleColumnChange(index, 'data_type', Array.isArray(val) ? val[0] : val)
                  }
                />
              </div>
            </div>

            {/* Row 2: Query (full width, indented to align with name) */}
            <div className="mt-2 pl-9">
              <Input
                id={`col-query-${index}`}
                type="text"
                label={t('AI Query')}
                placeholder={t('e.g., What is the effective date of the agreement?')}
                value={col.query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleColumnChange(index, 'query', e.target.value)
                }
                disabled={isReadOnly}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer: add column + count */}
      {columns.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Badge color="gray">{columns.length}</Badge>
            <span>{t('column' + (columns.length !== 1 ? 's' : '') + ' defined')}</span>
          </div>
          {!isReadOnly && (
            <Button
              type="button"
              variant="neutral"
              size="small"
              onClick={addColumn}
              data-tour-id="table-col-add-button"
            >
              <PiPlusLight className="w-4 h-4 mr-1" />
              {t('Add Column')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Category Options ──────────────────────────────────────────────────

const categoryOptions: DropdownOption[] = [
  { value: '', label: 'None' },
  { value: 'due_diligence', label: 'Due Diligence' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'litigation', label: 'Litigation' },
  { value: 'ip', label: 'Intellectual Property' },
  { value: 'employment', label: 'Employment' },
  { value: 'general', label: 'General' },
];

// ─── Main Form ─────────────────────────────────────────────────────────

interface TableColumnsFormProps {
  templateId?: string;
}

const TableColumnsForm: React.FC<TableColumnsFormProps> = ({ templateId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isEditing = !!templateId;

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingTemplate, setExistingTemplate] = useState<any>(null);
  const [isOrgWide, setIsOrgWide] = useState(false);

  const isGlobal = existingTemplate?.is_global;

  // ─── Tour ──────────────────────────────────────────────────────────
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'table-columns-form',
    steps: tableColumnsFormTourSteps,
    readyToShow: !isLoading && !isGlobal,
    autoStartDelay: 600,
  });

  const { isRunning: isColTourRunning, handleJoyrideCallback: handleColTourCallback } = useFeatureTour({
    tourId: 'table-column-items',
    steps: tableColumnItemTourSteps,
    readyToShow: !isLoading && !isGlobal,
    autoStartDelay: 600,
  });

  // ─── Load data ──────────────────────────────────────────────────────

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const promises: Promise<any>[] = [dispatch(fetchOrgPeople())];
        if (templateId) {
          promises.push(dispatch(getTemplate(templateId) as any));
        }
        const results = await Promise.all(promises);
        if (templateId && results[1]) {
          setExistingTemplate(results[1]);
          setIsOrgWide(!!results[1].is_org_wide);
        }
      } catch (error: any) {
        console.error('[TableColumnsForm] Error loading data:', error?.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [dispatch, templateId]);

  // ─── Initial Values ─────────────────────────────────────────────────

  const initialValues = useMemo(() => ({
    name: existingTemplate?.name || '',
    description: existingTemplate?.description || '',
    category: existingTemplate?.category || '',
    columns: existingTemplate?.columns?.length
      ? existingTemplate.columns.map((c: any) => ({
          name: c.name || '',
          query: c.query || '',
          data_type: c.data_type || 'text',
        }))
      : [{ name: '', query: '', data_type: 'text' as ColumnDataType }],
    is_org_wide: !!existingTemplate?.is_org_wide,
    collaborators: (existingTemplate?.shared_with || []).map((u: any) => ({
      user_id: u.user_id,
      collaborator_uuid: u.user_id,
      access: (u.access === 'admin' ? 'edit' : u.access) as 'edit' | 'view',
    })).filter((c: any) => c.access !== 'admin'),
  }), [existingTemplate]);

  // ─── Form Sections ──────────────────────────────────────────────────

  const formSections: BaseFormSectionConfig[] = useMemo(() => {
    const sections: BaseFormSectionConfig[] = [
      {
        dataTourId: 'table-cols-details',
        fields: [
          {
            name: 'name',
            type: 'input',
            label: t('Template Name'),
            placeholder: t('e.g., Due Diligence — Standard Review'),
            required: true,
            gridSize: 'full',
            disabled: isGlobal,
          } as BaseFormFieldConfig,
          {
            name: 'description',
            type: 'textarea',
            label: t('Description'),
            placeholder: t('What is this template used for?'),
            rows: 2,
            gridSize: 'full',
            disabled: isGlobal,
          } as BaseFormFieldConfig,
          {
            name: 'category',
            type: 'dropdown',
            label: t('Category'),
            options: categoryOptions,
            placeholder: t('Select a category'),
            gridSize: 'full',
          } as BaseFormFieldConfig,
        ],
      },
      {
        dataTourId: 'table-cols-columns',
        fields: [
          {
            name: 'columns',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: ColumnsEditor,
            customProps: { isGlobal },
          } as BaseFormFieldConfig,
        ],
      },
    ];

    // Only include sharing section for non-global templates
    if (!isGlobal) {
      sections.push({
        dataTourId: 'table-cols-sharing',
        title: t('Sharing'),
        description: t('Control who can access this template.'),
        fields: [
          {
            name: 'is_org_wide',
            type: 'custom',
            label: '',
            gridSize: 'full',
            customComponent: OrgWideToggle,
            customProps: { title: t('Organization-wide template') },
            disabled: isGlobal,
          } as BaseFormFieldConfig,
          {
            name: 'collaborators',
            type: 'collaboratorSelector',
            label: t('Access'),
            gridSize: 'full',
            disabled: isGlobal,
            hidden: isOrgWide,
          } as BaseFormFieldConfig,
        ],
      });
    }

    return sections;
  }, [t, isGlobal, isOrgWide]);

  // ─── Validation ─────────────────────────────────────────────────────

  const schema = useMemo(() => {
    const fields: any = {};
    fields['name'] = (value: any) => {
      if (!value || !String(value).trim()) {
        return { isValid: false, error: t('Template name is required') };
      }
      return { isValid: true };
    };
    fields['description'] = () => ({ isValid: true });
    fields['category'] = () => ({ isValid: true });
    fields['columns'] = (value: any) => {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return { isValid: false, error: t('At least one column is required') };
      }
      for (let i = 0; i < value.length; i++) {
        if (!value[i].name?.trim()) {
          return { isValid: false, error: t('Column {{n}} needs a name', { n: i + 1 }) };
        }
        if (!value[i].query?.trim()) {
          return { isValid: false, error: t('Column {{n}} needs a query', { n: i + 1 }) };
        }
      }
      return { isValid: true };
    };
    fields['is_org_wide'] = () => ({ isValid: true });
    fields['collaborators'] = () => ({ isValid: true });
    return new FormSchema(fields);
  }, [t]);

  // ─── Submit ─────────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    async (formData: Record<string, any>) => {
      setIsSubmitting(true);
      try {
        const orgWide = !!formData.is_org_wide;
        const collaborators: Collaborator[] = formData.collaborators || [];
        const payload = {
          name: String(formData.name).trim(),
          description: formData.description?.trim() || undefined,
          category: formData.category || undefined,
          columns: (formData.columns || []).map((col: ColumnItem, i: number) => ({
            name: col.name.trim(),
            query: col.query.trim(),
            data_type: col.data_type,
            sort_order: i,
          })),
          is_org_wide: orgWide,
          shared_with: orgWide
            ? []
            : collaborators.map((c) => ({
                id: c.user_id,
                collaborator_uuid: c.collaborator_uuid,
                access: c.access || 'view',
              })),
        };

        let result;
        if (isEditing && templateId) {
          result = await dispatch(updateTemplate(templateId, payload) as any);
        } else {
          result = await dispatch(createTemplate(payload) as any);
        }

        if (result) {
          history.push('/templates?type=table-columns');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, isEditing, templateId, history]
  );

  // ─── Cancel ─────────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    history.push('/templates?type=table-columns');
  }, [history]);

  // ─── Sync org-wide state for conditional rendering ──────────────────

  const handleValuesChange = useCallback((values: Record<string, any>) => {
    const newOrgWide = !!values.is_org_wide;
    if (newOrgWide !== isOrgWide) {
      setIsOrgWide(newOrgWide);
    }
  }, [isOrgWide]);

  // ─── Render ─────────────────────────────────────────────────────────

  if (isLoading) {
    return <FormSkeleton />;
  }

  // Global PONS templates: fully static read-only view
  if (isGlobal) {
    const cols = initialValues.columns || [];
    return (
      <div className="max-w-4xl mx-auto py-8 px-6 space-y-8">
        {/* Back button */}
        <button
          type="button"
          onClick={() => history.push('/templates?type=table-columns')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
        >
          <PiArrowLeftLight className="w-4 h-4" />
          {t('Back to Templates')}
        </button>

        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {initialValues.name || t('Untitled')}
          </h1>
          {initialValues.description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{initialValues.description}</p>
          )}
          {initialValues.category && (
            <div className="mt-2">
              <Badge color="blue" className="capitalize">
                {t((initialValues.category || 'general').replace(/_/g, ' '))}
              </Badge>
            </div>
          )}
        </div>

        {/* Columns */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base text-gray-900 dark:text-white">{t('Columns')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {t('These are the columns (queries) that will be applied to each document.')}
              </p>
            </div>
            <Badge color="gray">{cols.length}</Badge>
          </div>
          <div className="space-y-3">
            {cols.map((col: ColumnItem, index: number) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center pt-0.5">
                    <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 bg-gray-200/60 dark:bg-gray-700/50 w-6 h-6 rounded-full flex items-center justify-center">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{col.name}</span>
                      <Badge color="gray" className="text-xs">{col.data_type}</Badge>
                    </div>
                    {col.query && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{col.query}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PONS attribution */}
        <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 pt-4 border-t border-gray-200 dark:border-gray-700">
          <img src="/logosvg.svg" alt="PONS" className="h-4 w-4" />
          <span>{t('This is a default template provided by PONS.')}</span>
        </div>
      </div>
    );
  }

  return (
    <>
    <BaseForm
      title={isEditing ? t('Edit Table Columns Template') : t('New Table Columns Template')}
      description={t('Define reusable column templates for batch document analysis.')}
      sections={formSections}
      schema={schema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      onValuesChange={handleValuesChange}
      submitLabel={isEditing ? t('Save Changes') : t('Create Template')}
      cancelLabel={t('Cancel')}
      isSubmitting={isSubmitting}
      maxWidth="max-w-4xl"
      hideActions={isGlobal}
    />
    <FeatureTour
      steps={tableColumnsFormTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    <FeatureTour
      steps={tableColumnItemTourSteps}
      run={isColTourRunning}
      onCallback={handleColTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </>
  );
};

export default TableColumnsForm;
